import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { patchCommunicationAssignmentProgress } from "../../services/api/communicationLearning";
import { useCommunicationAssignments } from "../../modules/student-dashboard/hooks/useCommunicationAssignments";
import { ROUTES } from "../../utils/constants";
import { showErrorToast } from "../../utils/errorHandler";

type Props = {
  /** Called after progress patch so parent can refresh without full page reload */
  onListChange?: () => void;
  /** Increment from parent (e.g. header Refresh) to reload the list */
  refreshSignal?: number;
  /**
   * ``dashboard``: compact "From your teacher" strip for Home (open tasks only, navigate to tutor).
   * ``full``: assignments tab with progress actions.
   */
  variant?: "full" | "dashboard";
};

export const CommunicationAssignmentsPanel: React.FC<Props> = ({
  onListChange,
  refreshSignal = 0,
  variant = "full",
}) => {
  const { t } = useTranslation();
  const { items, loading, error, reload } = useCommunicationAssignments();
  const loadError = error
    ? t("dashboard.studentHome.commAssignments.loadError")
    : null;

  useEffect(() => {
    if (refreshSignal > 0) void reload();
  }, [refreshSignal, reload]);

  useEffect(() => {
    if (error && variant !== "dashboard") {
      showErrorToast(loadError);
    }
  }, [error, variant, loadError]);

  const patchAndRefresh = async (
    assignmentId: string,
    body: { status: string; attempts_delta: number }
  ) => {
    await patchCommunicationAssignmentProgress(assignmentId, body);
    await reload();
    onListChange?.();
  };

  const openOnly =
    variant === "dashboard"
      ? items.filter((row) => {
          const a = row as Record<string, unknown>;
          const prog = (a.progress as Record<string, unknown>) || {};
          const s = String(prog.status ?? "not_started").toLowerCase();
          return s !== "completed";
        })
      : items;

  if (variant === "dashboard") {
    if (loading) {
      return (
        <Card className="mt-8 border-amber-200 dark:border-amber-800 bg-amber-50/95 dark:bg-amber-950/50">
          <CardContent className="pt-6 pb-6">
            <p className="text-sm text-amber-900/80 dark:text-amber-100/80">
              {t("dashboard.studentHome.commAssignments.loading")}
            </p>
          </CardContent>
        </Card>
      );
    }
    if (loadError) {
      return (
        <Card className="mt-8 border-amber-200 dark:border-amber-800 bg-amber-50/95 dark:bg-amber-950/50">
          <CardContent className="pt-6 pb-6">
            <p className="text-sm text-red-700 dark:text-red-300">{loadError}</p>
          </CardContent>
        </Card>
      );
    }
    if (openOnly.length === 0) {
      return null;
    }
    return (
      <Card className="mt-8 border-amber-200 dark:border-amber-800 bg-amber-50/95 dark:bg-amber-950/50 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs uppercase tracking-wide text-amber-900 dark:text-amber-100 font-semibold">
            {t("dashboard.studentHome.commAssignments.fromTeacher")}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <ul className="space-y-2">
            {openOnly.map((row) => {
              const a = row as Record<string, unknown>;
              const aid = String(a._id ?? "");
              const topicId = String(a.topic_id ?? "").trim();
              const tutorHref = topicId
                ? `${ROUTES.STUDENT_COMMUNICATION_TUTOR}?topic_id=${encodeURIComponent(topicId)}`
                : ROUTES.STUDENT_COMMUNICATION_TUTOR;
              return (
                <li
                  key={aid}
                  className="text-sm text-slate-800 dark:text-slate-100 flex flex-wrap items-center gap-x-3 gap-y-1"
                >
                  <span className="font-medium">
                    {String(a.title ?? t("dashboard.studentHome.commAssignments.practiceTask"))}
                  </span>
                  <span className="text-slate-600 dark:text-slate-400">
                    {t("dashboard.studentHome.commAssignments.due", {
                      date: String(a.due_date ?? "—"),
                    })}
                  </span>
                  <Link to={tutorHref} className="text-app-primary dark:text-app-primary/80 font-semibold underline">
                    {t("dashboard.studentHome.commAssignments.practiceTopic")}
                  </Link>
                  <Link
                    to={ROUTES.STUDENT_ASSIGNMENTS_COMMUNICATION_TAB}
                    className="text-xs text-slate-600 dark:text-slate-400 underline"
                  >
                    {t("dashboard.studentHome.commAssignments.myAssignments")}
                  </Link>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('dashboard.studentHome.commAssignments.fullTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-slate-500">{t('dashboard.studentHome.commAssignments.fullLoading')}</p>
        ) : loadError ? (
          <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('dashboard.studentHome.commAssignments.fullEmpty')}{' '}
            <Link to={ROUTES.STUDENT_COMMUNICATION_TUTOR} className="font-semibold text-app-primary underline">
              {t('dashboard.studentHome.commAssignments.fullEmptyLink')}
            </Link>
          </p>
        ) : (
          <ul className="space-y-3">
            {items.map((row) => {
              const a = row as Record<string, unknown>;
              const prog = (a.progress as Record<string, unknown>) || {};
              const aid = String(a._id ?? "");
              const topicId = String(a.topic_id ?? "").trim();
              const tutorHref = topicId
                ? `${ROUTES.STUDENT_COMMUNICATION_TUTOR}?topic_id=${encodeURIComponent(topicId)}`
                : ROUTES.STUDENT_COMMUNICATION_TUTOR;
              return (
                <li
                  key={aid}
                  className="p-4 rounded-xl border border-purple-100 bg-gradient-to-r from-white to-violet-50/50 dark:border-purple-900/40 dark:from-slate-900 dark:to-violet-950/30"
                >
                  <div className="font-medium text-slate-900 dark:text-white">{String(a.title ?? "")}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                    {t('dashboard.studentHome.commAssignments.fullDue', {
                      date: String(a.due_date ?? ""),
                    })}
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">
                    {t('dashboard.studentHome.commAssignments.fullStatus', {
                      status: String(prog.status ?? "not_started"),
                    })}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 items-center">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void patchAndRefresh(aid, { status: "in_progress", attempts_delta: 1 })}
                    >
                      {t('dashboard.studentHome.commAssignments.markInProgress')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => void patchAndRefresh(aid, { status: "completed", attempts_delta: 0 })}
                    >
                      {t('dashboard.studentHome.commAssignments.markCompleted')}
                    </Button>
                    <Link
                      to={tutorHref}
                      className="text-sm font-semibold text-app-primary dark:text-app-primary/80 underline"
                    >
                      {t('dashboard.studentHome.commAssignments.openInTutor')}
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
